import { WritersRoomPhase } from '@/hooks/useWritersRoom';
import {
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty,
  Refresh,
} from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

interface PipelineStep {
  key: string;
  label: string;
  phases: WritersRoomPhase[];
}

type StepStatus = 'success' | 'loading' | 'error' | 'warning' | 'pending';

function getStepStatus(
  stepKey: string,
  stepPhases: WritersRoomPhase[],
  phase: WritersRoomPhase,
  isSnapshotCreated: boolean,
  error: string | null,
  steps: PipelineStep[],
): StepStatus {
  if (stepKey === 'snapshot' && isSnapshotCreated) return 'success';

  const currentPhaseIndex = steps.findIndex(s => s.phases.includes(phase));
  const stepIndex = steps.findIndex(s => s.key === stepKey);

  if (phase === 'approved' || phase === 'ready') return 'success';

  if (stepPhases.includes(phase)) {
    if (error) return 'error';
    if (phase === 'needs_fixes') return 'warning';
    return 'loading';
  }

  if (currentPhaseIndex > stepIndex && currentPhaseIndex !== -1) return 'success';

  if (stepKey === 'snapshot' && phase === 'prepared') return 'success';

  return 'pending';
}

function StatusIcon({ status }: { status: StepStatus }) {
  switch (status) {
    case 'success': return <CheckCircle color="success" sx={{ fontSize: 18 }} />;
    case 'loading': return <CircularProgress size={16} />;
    case 'error': return <ErrorIcon color="error" sx={{ fontSize: 18 }} />;
    case 'warning': return <ErrorIcon color="warning" sx={{ fontSize: 18 }} />;
    default: return <HourglassEmpty sx={{ fontSize: 18, opacity: 0.3 }} />;
  }
}

interface WritersRoomPipelineListProps {
  phase: WritersRoomPhase;
  isSnapshotCreated: boolean;
  error: string | null;
  onRedo: (phase: WritersRoomPhase) => void;
}

export function WritersRoomPipelineList({
  phase,
  isSnapshotCreated,
  error,
  onRedo,
}: WritersRoomPipelineListProps) {
  const { t } = useTranslation();

  const steps: PipelineStep[] = [
    { key: 'snapshot', label: t('writers_room.phases.snapshot'), phases: ['snapshot'] },
    { key: 'writing', label: t('writers_room.phases.writing'), phases: ['writing'] },
    { key: 'editing', label: t('writers_room.phases.editing'), phases: ['editing'] },
    { key: 'validating', label: t('writers_room.phases.validating'), phases: ['validating', 'needs_fixes', 'auto_fixing'] },
    { key: 'approving', label: t('writers_room.phases.approved'), phases: ['approved', 'ready'] },
  ];

  return (
    <>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 600, textTransform: 'uppercase', mb: 1, display: 'block' }}
      >
        {t('writers_room.pipeline_status')}
      </Typography>

      <List dense sx={{ py: 0 }}>
        {steps.map((step) => {
          const status = getStepStatus(step.key, step.phases, phase, isSnapshotCreated, error, steps);
          return (
            <ListItem
              key={step.key}
              sx={{ px: 0 }}
              secondaryAction={
                status === 'success' && (
                  <Tooltip title={t('common.redo')}>
                    <IconButton
                      size="small"
                      onClick={() => onRedo(step.phases[0])}
                      sx={{ color: 'text.disabled', '&:hover': { color: 'warning.main' } }}
                    >
                      <Refresh sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )
              }
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                <StatusIcon status={status} />
              </ListItemIcon>
              <ListItemText
                primary={step.label}
                primaryTypographyProps={{
                  variant: 'body2',
                  fontWeight: status === 'loading' ? 700 : 400,
                  color: status === 'pending' ? 'text.disabled' : 'text.primary',
                }}
              />
            </ListItem>
          );
        })}
      </List>
    </>
  );
}
